import { motion } from "framer-motion";
import { Clock, MapPin, Phone, Calendar } from "lucide-react";
import { useRef, useEffect } from "react";

const WORSHIP_VIDEO = `${process.env.REACT_APP_BACKEND_URL}/api/media/worship-video`;

export default function ServiceTimes() {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.controls = false;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    
    const tryPlay = () => {
      v.muted = true;
      v.controls = false;
      const p = v.play();
      if (p) p.catch(() => {});
    };
    
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) tryPlay(); },
      { threshold: 0.1 }
    );
    observer.observe(v);
    tryPlay();
    const playOnInteract = () => { tryPlay(); };
    document.addEventListener("touchstart", playOnInteract, { once: true });
    document.addEventListener("click", playOnInteract, { once: true });
    return () => {
      observer.disconnect();
      document.removeEventListener("touchstart", playOnInteract);
      document.removeEventListener("click", playOnInteract);
    };
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 px-6 relative overflow-hidden"
    >
      {/* Background video */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        src={WORSHIP_VIDEO}
        controls={false}
        className="absolute inset-0 w-full h-full object-cover opacity-[0.90]"
        style={{ pointerEvents: "none", WebkitAppearance: "none" }}
        webkit-playsinline="true"
      />
      <div className="absolute inset-0 bg-white/15" />

      <div className="max-w-7xl mx-auto relative">
        <motion.div
          className="text-center mb-16 bg-white/90 backdrop-blur-sm rounded-3xl py-8 px-6 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-navy/10 text-navy text-xs font-body font-bold uppercase tracking-[0.2em] mb-6">
            <Calendar className="w-3.5 h-3.5" />
            Service Times
          </span>
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-navy mb-4">
            Join Us in Worship
          </h2>
          <p className="font-body text-slate-700 text-base max-w-xl mx-auto">
            We'd love to welcome you to our services. Come and experience the power of God!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Time card */}
          <motion.div
            className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="service-time-card"
          >
            <div className="w-14 h-14 rounded-2xl bg-navy/5 flex items-center justify-center mb-6">
              <Clock className="w-7 h-7 text-navy" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-navy mb-2">
              Sunday Service
            </h3>
            <div className="space-y-4 mt-6">
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-navy/3">
                <div className="w-10 h-10 rounded-xl bg-navy flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy text-lg">11:00 AM &mdash; 1:00 PM</p>
                  <p className="font-body text-slate-500 text-sm">Every Sunday</p>
                </div>
              </div>
            </div>
            <p className="font-body text-slate-500 text-sm mt-6 italic">
              "Jesus Christ, the same yesterday, today, and forever." &mdash; Hebrews 13:8
            </p>
          </motion.div>

          {/* Location card */}
          <motion.div
            className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="location-card"
          >
            <div className="w-14 h-14 rounded-2xl bg-cgreen/10 flex items-center justify-center mb-6">
              <MapPin className="w-7 h-7 text-cgreen" />
            </div>
            <h3 className="font-heading font-bold text-2xl text-navy mb-2">
              Our Location
            </h3>
            <div className="space-y-4 mt-6">
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-cgreen/5">
                <div className="w-10 h-10 rounded-xl bg-cgreen flex items-center justify-center shrink-0 mt-0.5">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy">Walton Community Hall</p>
                  <p className="font-body text-slate-600 text-sm mt-1">
                    274 High Street, Walton,<br />
                    Felixstowe, IP11 9DS
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-crimson/5">
                <div className="w-10 h-10 rounded-xl bg-crimson flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-heading font-bold text-navy">Pastor Samson Jegede</p>
                  <p className="font-body text-slate-600 text-sm">07738998165</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Map embed */}
        <motion.div
          className="mt-12 rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-slate-100 max-w-5xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          data-testid="map-embed"
        >
          <iframe
            title="Walton Community Hall Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2453.8!2d1.3357!3d51.9536!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d9a1234567890%3A0x1234567890abcdef!2s274+High+Street%2C+Walton%2C+Felixstowe+IP11+9DS!5e0!3m2!1sen!2suk!4v1234567890"
            width="100%"
            height="350"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </motion.div>
      </div>
    </section>
  );
}
